// src/pages/pedidos/PesqPedidoPage.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { fetchData } from '../../services/Api';
import { formatCurrency } from '../../utils/numberUtils';
import { formatDate, formatDateInput, formatTime } from '../../utils/dateTimeUtils';
import { useMessage } from '../../contexts/geral/MessageContext';
import PageContainer from '../../components/geral/PageContainer';
import PageHeader from '../../components/geral/PageHeader';
import PageContent from '../../components/geral/PageContent';
import PageFooter from '../../components/geral/PageFooter';
import Input from '../../components/input/Input';
import ModalDetalhePedido from './ModalDetalhePedido';
import { usePedido } from '../../contexts/pedidos/PedidoContext';
import Select from '../../components/input/Select';
import { useConfigModulo } from '../../contexts/administrativo/ConfigModuloContext';
import { getNumeroPedido } from '../../utils/pedidoUtils';
import { getStatusPedidoDescricao, statusPedidoOptions } from '../../models/pedidos/StatusPedido';
import { getResumoPedidosPDFService } from '../../services/pedidos/PedidoService';
import { usePrinter } from '../../contexts/geral/PrinterContext';

const PesqPedidoPage = () => {
  const { showMessage } = useMessage();
  const { configModuloSel } = useConfigModulo();
  const [periodoInicial, setPeriodoInicial] = useState('');
  const [horaInicial, setHoraInicial] = useState('00:00');
  const [periodoFinal, setPeriodoFinal] = useState('');
  const [horaFinal, setHoraFinal] = useState('23:50');
  const [statusPedido, setStatusPedido] = useState(-1);
  const [formaPagId, setFormaPagId] = useState(-1);
  const { setPedidoSel } = usePedido();
  const { imprimirPdfBlob } = usePrinter();
  const [pedidos, setPedidos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });
  const [gerandoResumo, setGerandoResumo] = useState(false);
  
  const statusCombo = [{ value: -1, label: "Todos" }, ...statusPedidoOptions];

  const tipoPagamentoCombo = [
    { value: -1, label: "Todos" },
    { value: 1, label: "Dinheiro" },
    { value: 2, label: "Pix" },
    { value: 3, label: "Débito" },
    { value: 4, label: "Crédito" },
  ];
  

  const buscarPedidos = useCallback(async () => {
  try {
    const dataHoraInicial = `${periodoInicial}T${horaInicial || '00:00'}:00`;
    const dataHoraFinal = `${periodoFinal}T${horaFinal || '23:50'}:59`;
    const data = await fetchData('/Pedido/GetPedidosByFilter', {
      dataIni: dataHoraInicial,
      dataFim: dataHoraFinal,
      statusPedido: statusPedido === -1 ? null : statusPedido,
      formaPagId,
    });

    // Ordena do mais recente para o mais antigo
    setPedidos(
      data.sort((a, b) => new Date(b.dataHoraPedido) - new Date(a.dataHoraPedido))
    );
  } catch (err) {
    showMessage(`Erro ao buscar pedidos: ${err}`, 'danger');
  }
}, [periodoInicial, horaInicial, periodoFinal, horaFinal, statusPedido, formaPagId, showMessage]);

  useEffect(() => {
    const dataAtual = new Date();
    setPeriodoInicial(formatDateInput(dataAtual));
    setPeriodoFinal(formatDateInput(dataAtual));
  }, []);

  useEffect(() => {
    if (periodoInicial && horaInicial && periodoFinal && horaFinal) {
      buscarPedidos();
    }
  }, [periodoInicial, horaInicial, periodoFinal, horaFinal, buscarPedidos]);

  const totalFrete = useMemo(
    () => pedidos.reduce((total, pedido) => total + Number(pedido?.taxaEntrega || 0), 0),
    [pedidos]
  );

  const totalPedidos = useMemo(
    () => pedidos.reduce((total, pedido) => total + Number(pedido?.valorTotal || 0), 0),
    [pedidos]
  );

  const handleImprimir = async () => {
    if (!periodoInicial || !horaInicial || !periodoFinal || !horaFinal) {
      showMessage('Informe as datas e horas inicial e final para gerar o resumo.', 'warning');
      return;
    }

    const dataHoraInicial = `${periodoInicial}T${horaInicial}:00`;
    const dataHoraFinal = `${periodoFinal}T${horaFinal}:59`;

    if (dataHoraInicial > dataHoraFinal) {
      showMessage('A data e hora inicial não podem ser posteriores à data e hora final.', 'warning');
      return;
    }

    setGerandoResumo(true);
    try {
      const pdf = await getResumoPedidosPDFService({
        dataIni: dataHoraInicial,
        dataFim: dataHoraFinal,
        statusPedido: statusPedido === -1 ? null : statusPedido,
        formaPagId: formaPagId === -1 ? null : formaPagId,
        paperWidthMm: 77,
      });
      await imprimirPdfBlob(pdf, {
        frameId: 'resumo-pedidos-pdf-print-frame',
        titulo: 'Resumo de pedidos',
        mensagemErro: 'Não foi possível abrir a impressão do resumo de pedidos.',
      });
    } catch (error) {
      showMessage(`Erro ao gerar resumo dos pedidos: ${error?.message || error}`, 'danger');
    } finally {
      setGerandoResumo(false);
    }
  };

  const pedidosOrdenados = useMemo(() => {
    const { campo, direcao } = ordenacao;

    if (!campo) {
      return pedidos;
    }

    const multiplicador = direcao === 'asc' ? 1 : -1;

    return [...pedidos].sort((a, b) => {
      const valorA = campo === 'status'
        ? getStatusPedidoDescricao(a?.statusPedido)
        : campo === 'tipoEntrega'
          ? a?.tipoEntrega?.descricao
          : campo === 'formaPag'
            ? a?.formaPag?.descricao
            : a?.[campo];
      const valorB = campo === 'status'
        ? getStatusPedidoDescricao(b?.statusPedido)
        : campo === 'tipoEntrega'
          ? b?.tipoEntrega?.descricao
          : campo === 'formaPag'
            ? b?.formaPag?.descricao
            : b?.[campo];

      if (campo === 'id' || campo === 'taxaEntrega' || campo === 'valorTotal') {
        return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
      }

      if (campo === 'dataHoraPedido') {
        return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
      }

      return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
        sensitivity: 'base',
      }) * multiplicador;
    });
  }, [pedidos, ordenacao]);

  const alterarOrdenacao = (campo) => {
    setOrdenacao((atual) => ({
      campo,
      direcao: atual.campo === campo
        ? (atual.direcao === 'asc' ? 'desc' : 'asc')
        : 'asc',
    }));
  };

  const renderOrdenadorColuna = (campo, titulo, style = {}) => {
    const ativa = ordenacao.campo === campo;

    return (
      <button
        type="button"
        onClick={() => alterarOrdenacao(campo)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          fontWeight: 'bold',
          color: 'inherit',
          cursor: 'pointer',
          width: '100%',
          textAlign: style.textAlign || 'left',
        }}
      >
        {titulo}
        {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
      </button>
    );
  };

  const handleVerDetalhes = async (pedido) => {
        try {             
            const result = await fetchData('/Pedido/GetPedidoById', { pedidoId:pedido.id });  
            setPedidoSel(result);         
            setModalOpen(true);
        } catch (error) {
            showMessage(`Erro ao buscar detalhes do resumo: ${error}`, 'danger');
        }
    };

    const handleCloseDetalhes = async () => {
        try {

          buscarPedidos();   
      
          setModalOpen(false);
        } catch (error) {
          showMessage(`Erro ao atualizar o pedido: ${error}`, 'danger');
        }
      };
  
  return (
    <PageContainer>
      <h2 className="page-title">Pesquisa de Pedidos</h2>
      <PageHeader>
        <div className="group-div">
          <Input
            label="Período Inicial"
            type="date"
            value={periodoInicial}
            onChange={(e) => setPeriodoInicial(e.target.value)}
            size={150}
          />
          <Input
            label="Hora Inicial"
            type="time"
            value={horaInicial}
            onChange={(e) => setHoraInicial(e.target.value)}
            size={90}
          />
          <Input
            label="Período Final"
            type="date"
            value={periodoFinal}
            onChange={(e) => setPeriodoFinal(e.target.value)}
            size={150}
          />
          <Input
            label="Hora Final"
            type="time"
            value={horaFinal}
            onChange={(e) => setHoraFinal(e.target.value)}
            size={90}
          />

        <Select
            label="Status"
            value={statusPedido}
            onChange={(e) => setStatusPedido(parseInt(e.target.value))}
            options={statusCombo}
            size={250}
        />    
        <Select
            label="Forma Pág."
            value={formaPagId}
            onChange={(e) => setFormaPagId(parseInt(e.target.value))}
            options={tipoPagamentoCombo}
            size={150}
        />
        </div>
        <button className="btn btn-search" onClick={buscarPedidos}>Buscar</button>
      </PageHeader>

      <PageContent>
        <table className="page-content-table">
          <thead>
            <tr>
              <th style={{ minWidth: '50px' }}>{renderOrdenadorColuna('id', 'ID')}</th>
              <th style={{ minWidth: '90px' }}>{renderOrdenadorColuna('status', 'Status')}</th>
              <th>{renderOrdenadorColuna('nomeCliente', 'Cliente')}</th>
              <th style={{ minWidth: '90px' }}>{renderOrdenadorColuna('tipoEntrega', 'Entrega')}</th>
              <th style={{ minWidth: '120px' }}>{renderOrdenadorColuna('formaPag', 'Forma Pág.')}</th>
              <th style={{ minWidth: '150px' }}>{renderOrdenadorColuna('dataHoraPedido', 'Data do Pedido')}</th>
              <th style={{ textAlign: 'right', minWidth: '85px' }}>
                {renderOrdenadorColuna('taxaEntrega', 'Frete', { textAlign: 'right' })}
              </th>
              <th style={{ textAlign: 'right', minWidth: '100px' }}>
                {renderOrdenadorColuna('valorTotal', 'Valor Total', { textAlign: 'right' })}
              </th>
              <th style={{ textAlign: 'center', minWidth: '110px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {pedidosOrdenados.map((pedido) => (
              <tr key={pedido.id}>
                <td>{getNumeroPedido(pedido, configModuloSel)}</td>
                <td>{getStatusPedidoDescricao(pedido?.statusPedido)}</td>
                
                <td>{pedido.nomeCliente}</td>
                <td>{pedido.tipoEntrega.descricao}</td>
                <td>{pedido.formaPag?.descricao}</td>
                <td>
                  {pedido.dataHoraPedido
                    ? `${formatDate(pedido.dataHoraPedido)} ${formatTime(pedido.dataHoraPedido)}`
                    : '-'}
                </td>
                <td style={{ textAlign: 'right', width:'100px' }}>{formatCurrency(pedido.taxaEntrega)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(pedido.valorTotal)}</td>
                <td style={{width:'110px'}}>
                    <button className="btn btn-table" onClick={() => handleVerDetalhes(pedido)}>Ver Detalhes</button>
                </td> 
              </tr>
            ))}
          </tbody>
        </table>
      </PageContent>

      <PageFooter>
        <div className="botoes-footer">
          <button
            type="button"
            className="btn btn-secondary btn-footer"
            onClick={handleImprimir}
            disabled={gerandoResumo}
          >
            {gerandoResumo ? 'Gerando...' : 'Imprimir resumo'}
          </button>
        </div>
        <div className="totais-footer">
          <span>Quantidade: {pedidos.length}</span>
          <span>Frete: {formatCurrency(totalFrete)}</span>
          <span>Total: {formatCurrency(totalPedidos)}</span>
        </div>
      </PageFooter>

      <ModalDetalhePedido
        isOpen={modalOpen}
        onClose={handleCloseDetalhes}
        //pedido={pedidoSel}
        />

    </PageContainer>
  );
};

export default PesqPedidoPage;
